import React, { useEffect, useState, useRef } from 'react';
import { Button, Col, Container, Heading, Icon, Label, Link, Loader, NewModal as Modal, Row, SelectInput, } from '@digitools/honest-react';
import { Language } from '../../../../../types/Language';
import { DocumentUploadDisplayTextEs } from '../../../../../components/documentUpload/language/DocumentUploadDisplayTextEs';
import { DocumentUploadDisplayTextEn } from '../../../../../components/documentUpload/language/DocumentUploadDisplayTextEn';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import useExperience from '../../../../../hooks/useExperience';
import useAuthentication from '../../../../../hooks/useAuthentication';
import styles from './RtwDetails.scss';
import { Experience } from 'src/types/Experience';
import { docMeta } from './docCategoryMapper';
import useTealium from '../../../../../hooks/useTealium';
import { TEALIUM_EVENT } from '../../../../../constants/tealiumConstants';
import useTranslate from 'src/hooks/useTranslate';
import { useDisclosure } from '@mantine/hooks';
import useDocumentUpload from 'src/components/documentUpload/useDocumentUpload';
import UploadErrorAlerts from 'src/components/documentUpload/UploadErrorAlerts';
const StyledCol = styled(Col) `
  border: 0.5px solid lightgrey;
  padding: 1.5%;
  box-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
`;
const StyledSerial = styled(Col) `
  background-color: #5b5d5e;
  color: white;
  display: flex;
  padding: 1em !important;
  margin-right: 1em;
  height: 2.5em;
  align-items: center;
  justify-content: center;
`;
const CategoryCol = styled(Col) `
  justify-content: space-between;
  display: flex;
`;
const StyledDeleteButton = styled(Button) `
  font-weight: bold;
  padding: 0;
  height: auto;
  &:hover {
    text-decoration: underline;
  }
`;
const StyledFileInput = styled('input') `
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  display: block;
  opacity: 0;
  width: 100%;
  height: 100%;
  cursor: pointer;
`;
const RtwDocUpload = (props) => {
    const { authHeader, user } = useAuthentication();
    const { language, experience } = useExperience();
    const { t } = useTranslation();
    const translate = useTranslate().t;
    const { trackEvent } = useTealium();
    // Guard analytics so a Tealium failure can't break the upload success/error flow.
    const safeTrackEvent = (params) => {
        try {
            trackEvent(params);
        }
        catch (error) {
            console.error('Tealium trackEvent failed', error);
        }
    };
    const languageFile = language === Language.SPANISH ? DocumentUploadDisplayTextEs : DocumentUploadDisplayTextEn;
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [documentCategory, setDocumentCategory] = useState('');
    const [productType, setProductType] = useState([]);
    const openButtonRef = useRef(null);
    const { errorMessage, apiFailureMessage, uploading, onUpload, clearStates: clearUploadStates } = useDocumentUpload({
        languageFile,
        interviewType: props.productName.toUpperCase(),
        experience,
        authHeader: authHeader(),
        onUploadSuccess: (response, file) => {
            const docMeta = documentCategory ? getDocMeta(documentCategory) : productType[0];
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.UPLOAD,
                event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
                event_name: 'open',
                event_version: 'return to work - details',
                event_results: '1', // Allowing only one file at a time
                form_result: 'gp_upload,' + docMeta.label + ',1',
            });
            setUploadedFiles(prev => [
                ...prev,
                {
                    documentName: file.name,
                    documentId: response.data,
                    label: docMeta.label,
                    documentTypeTitle: docMeta.title,
                    documentType: docMeta.type,
                },
            ]);
            setDocumentCategory('');
        },
        onApiError: () => {
            setDocumentCategory('');
            safeTrackEvent({
                event_action: TEALIUM_EVENT.EVENT_ACTION.UPLOAD,
                event_type: TEALIUM_EVENT.EVENT_TYPE.VIEW_STATUS,
                event_name: languageFile.apiFailureMessage + languageFile.contactUs,
                event_version: 'upload RTW document',
            });
        },
    });
    useEffect(() => {
        if (uploadedFiles.length > 0) {
            props.setUploaded(uploadedFiles);
            props.setUpdateDoc(false);
        }
    }, [uploadedFiles]);
    useEffect(() => {
        if (props.updateDoc) {
            setUploadedFiles([]);
        }
    }, [props.updateDoc]);
    useEffect(() => {
        if (user?.customerInContext?.smallMid) {
            // @ts-ignore
            const rtwSmallMid = translate(docMeta.docsNet.claimsEE).filter(e => e.isRtwEligible);
            props.productName !== 'LEAVE' && setProductType(rtwSmallMid);
        }
        else {
            if (props.productName.trim().toUpperCase() === 'LEAVE') {
                setProductType(translate(docMeta.s1.leaves).filter(e => e.isRtwEligible));
            }
            else if (experience === Experience.EMPLOYER) {
                setProductType(translate(docMeta.s1.claimsER).filter(e => e.isRtwEligible));
            }
            else {
                setProductType(translate(docMeta.s1.claimsEE).filter(e => e.isRtwEligible));
            }
        }
    }, [language]);
    const RenderOpenButton = ({ showModal }) => {
        const handleOnClick = () => {
            showModal();
        };
        if (props.isSuccess)
            return <></>;
        return (<Button ref={openButtonRef} buttonType={props.displayLabel ? 'secondary' : 'text'} size={'medium'} type={'button'} onClick={handleOnClick} data-testid={'openModal'} key={'openModal' + props.eventNum} aria-label={languageFile.uploadRTWLabel} style={{ padding: '0.5rem 0.5rem ', marginBottom: '3%' }}>
        {t('uploadRtwLabel')}
        <Icon name='upload' color={'secondary'} style={{ paddingLeft: '.25rem' }}/>
      </Button>);
    };
    const getDocMeta = (val) => {
        const metaObj = productType.filter((e) => e.label == val);
        return metaObj[0];
    };
    const getProduct = () => {
        if (props.productName === 'LEAVE') {
            return languageFile.leave;
        }
        else {
            return languageFile.claim;
        }
    };
    const uploadBtn = () => {
        return (<Col md={4}>
        <Label htmlFor={'file-upload' + props.eventNum}>
          <Icon color={'secondary'} name={'upload'} size={'medium'}/>
          <Link styles={{ marginLeft: '.5rem', fontSize: '16px', fontWeight: 'bold' }} data-testid={'uploadButton'}>
            {languageFile.uploadButtonText}
          </Link>
        </Label>
        <StyledFileInput data-testid={'fileUpload' + props.eventNum} type={'file'} id={'file-upload' + props.eventNum} onChange={onUpload} accept={'.gif, .jpg, .pdf, .pptx, .tif, .tiff, .xls, .xlsx, .doc, .docx'} aria-label='Upload Document'/>
      </Col>);
    };
    const updateCategory = (docDetails, val) => {
        let updatedCategory = uploadedFiles.map(e => {
            let metaDoc = getDocMeta(val);
            if (e.documentId == docDetails.documentId) {
                return { ...e, label: metaDoc.label, documentTypeTitle: metaDoc.title, documentType: metaDoc.type };
            }
            return e;
        });
        setUploadedFiles(updatedCategory);
    };
    const selectCategory = () => {
        let showCategory = uploadedFiles.length < 10 && !uploading && productType.length !== 1;
        return (<>
        {showCategory && (<CategoryCol md={5}>
            <StyledSerial>{uploadedFiles.length + 1}</StyledSerial>
            <SelectInput field={'documentCategory'} options={productType} data-testid={'documentCategory'} validateOnBlur={true} onChange={(e) => setDocumentCategory(e.target.value)} placeholderText={translate({
                    english: 'Select a document type',
                    spanish: 'Seleccione un tipo de documento',
                })} label={translate({
                    english: 'Document type',
                    spanish: 'Tipo de documento',
                })} initialValue={''}/>
          </CategoryCol>)}

        {(documentCategory || productType.length === 1) && !uploading && uploadBtn()}
      </>);
    };
    const renderColumns = () => {
        let showCategory = uploadedFiles.length < 10 && !uploading && productType.length !== 1;
        return (<>
        {uploadedFiles?.map((documentDetail, index) => {
                return (<>
            {showCategory && (<CategoryCol md={5}>
                <StyledSerial>{index + 1}</StyledSerial>
                <SelectInput label={translate({ english: 'Document Category', spanish: 'Categoría' })} placeholder={translate({ english: 'Select category', spanish: 'Seleccionar categoría' })} key={documentDetail.documentId} field={'doc-category'} options={productType} data-testid={'doc-category'} initialValue={documentDetail?.label} onChange={(e) => updateCategory(documentDetail, e.target.value)}/>
              </CategoryCol>)}
              <Col md={7}> {getSelectedDocuments(documentDetail)}</Col>
            </>);
            })}
      </>);
    };
    const getSelectedDocuments = (documentDetail, onPage) => {
        const handleDelete = () => {
            const newDocumentList = uploadedFiles.filter(file => file.documentId !== documentDetail.documentId);
            setUploadedFiles(newDocumentList);
            props.setUploaded(newDocumentList);
        };
        return (<Row key={documentDetail.documentId}>
        <StyledCol md={props.isSuccess ? 12 : 8}>
          <Row data-testid={'submitted-doc'} aria-labelledby={documentDetail.documentName}>
            <Icon styles={{ margin: '2px .5rem 0 1rem' }} name='documents'/> {documentDetail.documentName}
          </Row>
        </StyledCol>
        {!props.isSuccess && (<StyledCol md={onPage ? 4 : 3}>
            <StyledDeleteButton data-testid={'delete-doc'} aria-label={languageFile.delete} onClick={handleDelete}>
              <Icon color='secondary' className={styles.deleteIcon} name='trash'/> {languageFile.delete}
            </StyledDeleteButton>
          </StyledCol>)}
      </Row>);
    };
    const clearStates = () => {
        clearUploadStates();
        setDocumentCategory('');
    };
    const renderOnPageColumns = () => {
        return (<Col md={12}>
        {uploadedFiles.map(documentDetail => {
                return getSelectedDocuments(documentDetail, 'onpage');
            })}
      </Col>);
    };
    const [isOpen, { open, close }] = useDisclosure(false);
    return (<>
      <Modal data-testid={'uploadModal'} opened={isOpen} 
    // modalContainerStyle={{ maxWidth: '70rem' }}
    title={<Heading elemType={'h1'} style={{ lineHeight: 'normal' }}>
            {`${languageFile.headerText} ${getProduct()} #${props.eventNum}`}
          </Heading>} cancelButtonProps={{
            'data-testid': 'close',
            buttonType: 'secondary',
            type: 'button',
            'aria-label': 'close document upload'
        }} cancelButtonText={languageFile.closeText} onClose={() => {
            clearStates();
            close();
            setTimeout(() => {
                openButtonRef.current?.focus();
            }, 0);
        }} submitButton={false} size={'1100px'} closeOnClickOutside={true}>
        <Loader id='document-upload-spinner' isShown={uploading}>
          <Container>
            <UploadErrorAlerts errorMessage={errorMessage} apiFailureMessage={apiFailureMessage} languageFile={languageFile}/>
            <Row data-testid={'informationText'}>
              <p style={{
            fontSize: 'larger',
        }}>{`${languageFile.informationalMessage1} ${languageFile.informationalMessagePart}`}</p>
            </Row>
            <Row data-testid={'acceptableDocumentsText'}>
              {languageFile.informationalMessage2}
            </Row>
            <Row>
              <p style={{ fontWeight: 'bold', fontSize: 'larger' }}>{languageFile.uploadCategory}</p>
            </Row>
            <Row data-testid={'modalColumns'}>{renderColumns()}</Row>
            <Row data-testid={'modalCategory'}>{selectCategory()}</Row>
          </Container>
        </Loader>
      </Modal>
      <RenderOpenButton showModal={open}/>
      <Row data-testid={'OnPageColumns'}>{renderOnPageColumns()}</Row>
    </>);
};
export default RtwDocUpload;
